import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import allActions from "../actions/allActions";
import { FormSelect } from "../components/form/FormSelect";
import * as cState from "./certReducer";
import { TemplateCard } from "./components/TemplateCard";
import { PostmarkCard } from "./components/PostmarkCard";
import Tab from "../components/tabs/Tab";
import { Tabs } from "../components/tabs/Tabs";
import { Route, useLocation, useRouteMatch } from "react-router-dom";
import { ConfirmSend } from "./components/ConfirmSend";
import Loading from "../components/utils/Loading";
import { useFormatMessage } from "../utils/functions";
import CertParticipantTable from "./certParticipantTable/CertParticipantTable";
import { Switch } from "../components/utils/Switch";
import { CSVReader } from "react-papaparse";
import { ImportCSVData } from "../types/types";
import { v4 as uuidv4 } from "uuid";
import ConsoleHelper from "../utils/logger";
import BreadCrumb from "../components/breadcrumbs/BreadCrumb";
import { BreadRoute } from "../components/breadcrumbs/BreadRoute";

export const CertPage: React.FC = () => {
  //TODO: getLyytikey to errorReducer!
  //TODO: getLyytievents to errorReducer
  //TODO: getalllyytiparticipants to errorReducer!

  const dispatch = useDispatch();
  const location = useLocation();
  const { path, url } = useRouteMatch();
  const [activeTab, setActiveTab] = useState(null);
  const [importHeaders, setImportHeaders] = useState(true);
  const {
    isLoading,
    eventSourceOptions,
    certTemplateOptions,
    selectedSource,
    lyytiEvents,
    events,
    selectedEvent,
    participants,
    sendMultiple,
    selectedTemplate,
  } = useSelector(cState.getCertPageData);
  const templateOptions = [""].concat(certTemplateOptions);
  const eventOptions =
    selectedSource.toLowerCase() === "contactmate"
      ? [""].concat(events.map((e) => e.name))
      : [""].concat(lyytiEvents.map((l) => l.name));
  const intl = useIntl();
  const certTab = useFormatMessage("Cert.Tabs.Cert");
  const mailTab = useFormatMessage("Cert.Tabs.Mail");
  const confirmTab = useFormatMessage("Cert.Tabs.Send");
  const participantTab = useFormatMessage("Cert.Tabs.Participants");
  const selectEventsQuestion = useFormatMessage("Cert.CertPage.Events");
  const multiSendSwitch = useFormatMessage("Cert.CertPage.MultiSendSwitch");
  const headerRow =
    participants.length > 0
      ? Object.keys(participants[0]).length > 5
        ? Object.keys(participants[0]).slice(0, 5)
        : Object.keys(participants[0])
      : [];
  const crumbs = [
    { label: "certmenu", link: "/certmenu" },
    { label: "create", link: "/createcert" },
  ];

  const changeTemplate = (e: any) => {
    dispatch(allActions.certActions.changeSelectedTemplate(e.target.value));
  };

  const handleInputChange = (e: any, id: string) => {
    dispatch(allActions.formActions.updateValue(id, e.target.value, "newCert"));
  };

  const changeEventSource = (e: any) => {
    dispatch(allActions.certActions.changeEventSource(e.target.value));
  };

  const changeEvent = (e: any) => {
    const event =
      selectedSource.toLowerCase() === "lyyti"
        ? lyytiEvents.find((event) => event.name === e.target.value)
        : events.find((event) => event.name === e.target.value);
    dispatch(
      allActions.certActions.changeEvent(event.id, selectedSource, event.name)
    );
  };

  const changeSendType = (id: string) => {
    dispatch(allActions.certActions.changeSendType());
  };

  const getActiveTab = useCallback(() => {
    const pathArray = location.pathname.split("/");
    return pathArray.length === 2
      ? setActiveTab(certTab)
      : setActiveTab(pathArray[pathArray.length - 1]);
  }, [location, certTab]);

  const getCertNew = useCallback(() => {
    dispatch(allActions.certActions.startCertNewFetch());
    dispatch(allActions.certActions.getLyytikey());
  }, [dispatch]);

  const getAllEvents = useCallback(
    (source: string) => {
      if (source.toLowerCase() === "lyyti") {
        dispatch(allActions.eventActions.getAllLyytiEvents(intl.locale));
      }
      if (source.toLowerCase() === "contactmate")
        dispatch(
          allActions.eventActions.getAllEvents({ date: "0", limit: "100" })
        );
    },
    [dispatch, intl]
  );

  const handleOnDrop = (data: ImportCSVData[]) => {
    const dataToMap = importHeaders ? data.slice(1) : data;
    const participantData = dataToMap.map((imported) => {
      return Object.assign(
        {},
        Object.assign(
          { uuid: uuidv4() },
          ...imported.data.map((value: any, i: number) => {
            const key = importHeaders ? data[0].data[i] : `Column_${i}`;
            return { [key]: value };
          })
        )
      );
    });
    dispatch(allActions.certActions.importParticipants(participantData));
  };

  const handleOnError = (
    err: Error,
    file: any,
    inputElem: any,
    reason: any
  ) => {
    ConsoleHelper(err);
  };

  useEffect(() => {
    getAllEvents(selectedSource);
  }, [selectedSource, getAllEvents]);

  useEffect(() => {
    getCertNew();
  }, [getCertNew]);

  useEffect(() => {
    getActiveTab();
  }, [getActiveTab]);

  return (
    <div className='container w-screen h-full'>
      <BreadRoute>
        {crumbs.map((c, i) => {
          const last = i + 1 === crumbs.length;
          return (
            <BreadCrumb
              key={c.link}
              label={c.label}
              link={c.link}
              lastChild={last}
            />
          );
        })}
      </BreadRoute>
      <div className='flex'>
        <FormSelect
          id='eventSource'
          question={useFormatMessage("Cert.CertPage.EventSource")}
          handleInputChange={changeEventSource}
          options={eventSourceOptions}
          value={selectedSource}
        />
        {selectedSource === "CSV-import" ? (
          <>
            <Switch
              id='dataHasHeaders'
              header={"data has headers"}
              show={importHeaders}
              labelStyle={"text-sm italic"}
              divStyle={"mt-12"}
              switchAction={() => setImportHeaders(!importHeaders)}
            />
            <div className='mt-6 m-2'>
              <CSVReader onDrop={handleOnDrop} onError={handleOnError}>
                <span className='italic'>Drag here or click to upload</span>
              </CSVReader>
            </div>
          </>
        ) : selectedSource !== "" ? (
          <>
            <FormSelect
              id='eventCert'
              question={selectEventsQuestion}
              handleInputChange={changeEvent}
              options={eventOptions}
              value={selectedEvent}
            />
            <Switch
              id='certSendType'
              header={multiSendSwitch}
              show={sendMultiple}
              switchAction={changeSendType}
            />
          </>
        ) : null}
        <FormSelect
          id='newCertTemplateName'
          question={useFormatMessage("Cert.CertPage.SelectTemplate")}
          handleInputChange={changeTemplate}
          options={templateOptions}
          value={selectedTemplate}
        />
      </div>
      <Tabs>
        <Tab
          activeLabel={activeTab}
          label={participantTab}
          link={`${url}/${participantTab}`}
          hidden={participants.length === 0 || !sendMultiple}
        />
        <Tab activeLabel={activeTab} label={certTab} link={`${url}`} />
        <Tab
          activeLabel={activeTab}
          label={mailTab}
          link={`${url}/${mailTab}`}
        />
        <Tab
          activeLabel={activeTab}
          label={confirmTab}
          link={`${url}/${confirmTab}`}
        />
      </Tabs>
      <div className='container h-full max-h-3/4 w-full flex justify-center items-center border-r border-l border-b border-gray-400 overflow-auto scroller'>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Route
              path={`${path}/${participantTab}`}
              render={() => (
                <CertParticipantTable
                  list={participants}
                  headerRow={headerRow}
                />
              )}
            />
            <Route
              exact
              path={path}
              render={() => (
                <TemplateCard
                  templateName={selectedTemplate}
                  handleInputChange={handleInputChange}
                />
              )}
            />
            <Route
              path={`${path}/${mailTab}`}
              render={() => (
                <PostmarkCard
                  templateName={selectedTemplate}
                  handleInputChange={handleInputChange}
                />
              )}
            />
            <Route
              path={`${path}/${confirmTab}`}
              render={() => <ConfirmSend templateName={selectedTemplate} />}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CertPage;
